import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  Checklist,
  ColorPicker,
  Field,
  Input,
  Select,
  DatePicker // Added DatePicker
} from '@itds/elements/index.js';
import { Colors } from '@itds/Theme';
import { AgencyContext } from '@brokerage/contexts/AgencyContext';
import Constants from '@itds/Constants'; // Import US states

const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' }
];
const YES_NO_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' }
];
const CALC_TYPE_OPTIONS = [
  { value: 'Premium', label: 'Premium' },
  { value: 'Monthly Benefit', label: 'Monthly Benefit' },
  { value: 'Total Benefit', label: 'Total Benefit' },
  { value: 'Daily Amount', label: 'Daily Amount' },
  { value: 'Specified Amount', label: 'Specified Amount' }
];
const PAYMENT_SCHEDULE_OPTIONS = [
  { value: 'Single Premium', label: 'Single Premium' },
  { value: '5-Pay', label: '5-Pay' },
  { value: '10-Pay', label: '10-Pay' },
  { value: 'Pay to Age 65', label: 'Pay to Age 65' },
  { value: 'Pay To Age 100', label: 'Pay To Age 100' }
];
const BENEFIT_DURATION_OPTIONS = [
  { value: '6 Years', label: '6 Years' },
  { value: '7 Years', label: '7 Years' },
  { value: '8 Years', label: '8 Years' },
  { value: '9 Years', label: '9 Years' },
  { value: '10 Years', label: '10 Years' }
];
const REFUND_OPTIONS = [
  { value: 'minimum_ltc', label: 'Minimum LTC' },
  { value: 'one_time_step_up', label: 'One time Step-up' },
  { value: 'vested', label: 'Vested' }
];
const INFLATION_OPTIONS = [
  { value: '3_simple', label: '3% Simple' },
  { value: '3_compound', label: '3% Compound' },
  { value: '5_compound', label: '5% Compound' },
  { value: 'us_medical', label: 'US Medical Care Inflation' },
  { value: 'none', label: 'None' }
];

const getRateLimitValues = aq => ({
  rate_limit_replenishment_period_seconds:
    'rate_limit_replenishment_period_seconds' in aq ? aq.rate_limit_replenishment_period_seconds : '',
  rate_limit_token_limit: 'rate_limit_token_limit' in aq ? aq.rate_limit_token_limit : '',
  rate_limit_tokens_per_period: 'rate_limit_tokens_per_period' in aq ? aq.rate_limit_tokens_per_period : ''
});

const getMonthlyUsageValues = aq => ({
  monthly_request_limit: 'monthly_request_limit' in aq ? aq.monthly_request_limit : ''
});

const getRequiredFieldsOptions = aq => {
  if (Array.isArray(aq['required_fields_options'])) {
    return aq['required_fields_options'];
  }

  return [];
};

const General = ({ ltc_quoter = {}, handleSave, token, controlledMode }) => {
  const aq = ltc_quoter || {};
  const { member_names } = useContext(AgencyContext);
  const [errors, setErrors] = useState([]);
  const [data, setData] = useState({
    name: 'name' in aq ? aq.name : '',
    theme_color: 'theme_color' in aq ? aq.theme_color : '',
    member_id: 'member_id' in aq ? aq.member_id : '',
    hide_intro: 'hide_intro' in aq ? aq.hide_intro : false,
    required_fields: Array.isArray(aq['required_fields']) ? aq['required_fields'] : [],
    is_archived: 'is_archived' in aq ? aq.is_archived : false,
    required_fields_options: getRequiredFieldsOptions(aq),
    // Extra fields for Universal Quoter LTC Product Defaults with defaults
    dob: 'dob' in aq ? aq.dob : '',
    age: 'age' in aq ? aq.age : '',
    gender: 'gender' in aq ? aq.gender : 'Male',
    state: 'state' in aq ? aq.state : Constants.STATES[0]?.value || '',
    tobacco: 'tobacco' in aq ? aq.tobacco : 'Yes',
    married: 'married' in aq ? aq.married : 'Yes',
    calculation_type: 'calculation_type' in aq ? aq.calculation_type : CALC_TYPE_OPTIONS[0].value,
    amount: 'amount' in aq ? aq.amount : '',
    payment_schedule:
      'payment_schedule' in aq ? aq.payment_schedule : PAYMENT_SCHEDULE_OPTIONS[0].value,
    payment_frequency:
      'payment_frequency' in aq ? aq.payment_frequency : PAYMENT_SCHEDULE_OPTIONS[0].value,
    benefit_duration:
      'benefit_duration' in aq ? aq.benefit_duration : BENEFIT_DURATION_OPTIONS[0].value,
    lump_sum_premium: 'lump_sum_premium' in aq ? aq.lump_sum_premium : '',
    refund_of_premium: 'refund_of_premium' in aq ? aq.refund_of_premium : REFUND_OPTIONS[0].value,
    inflation_protection:
      'inflation_protection' in aq ? aq.inflation_protection : INFLATION_OPTIONS[0].value,
    exchange_1035: 'exchange_1035' in aq ? aq.exchange_1035 : 'No',
    exchange_1035_amount: 'exchange_1035_amount' in aq ? aq.exchange_1035_amount : '',
    rate_limit_enabled: 'rate_limit_enabled' in aq ? aq.rate_limit_enabled : false,
    ...getRateLimitValues(aq),
    monthly_limit_enabled: 'monthly_limit_enabled' in aq ? aq.monthly_limit_enabled : false,
    ...getMonthlyUsageValues(aq)
  });

  const handleChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleCheck = (name, e) => {
    const checked = !!e.target.checked;

    if (name === 'rate_limit_enabled' && !checked) {
      setData({
        ...data,
        rate_limit_enabled: checked,
        ...getRateLimitValues(aq)
      });

      return;
    }

    if (name === 'monthly_limit_enabled' && !checked) {
      setData({
        ...data,
        monthly_limit_enabled: checked,
        ...getMonthlyUsageValues(aq)
      });

      return;
    }

    setData({ ...data, [name]: checked });
  };

  const handleChecklistChange = (name, selected) => {
    setData({ ...data, [name]: selected });
  };

  const handleColorChange = (name, color) => {
    setData({ ...data, [name]: color.hex });
  };

  const handleDateChange = (name, value) => {
    setData({ ...data, [name]: value });
  };

  const handleSaveButton = () => {
    const errors = [];

    if (!data.name) {
      errors.push('name');
    }
    setErrors(errors);
    if (!errors.length) {
      handleSave(data);
    }
  };

  const handleArchiveToggle = () => {
    setData({ ...data, is_archived: !data.is_archived });
    handleSave({ is_archived: !data.is_archived });
  };

  const rateLimitsReadOnly = !data.rate_limit_enabled || !!data.is_archived;
  const monthlyUsageReadOnly = !data.monthly_limit_enabled || !!data.is_archived;
  const sectionHeaderStyle = {
    color: '#666',
    fontSize: 13,
    fontWeight: 600,
    margin: '0 0 12px'
  };

  return (
    <>
      <Field label='Name' style={{}}>
        <Input
          invalid={errors.includes('name')}
          onChange={handleChange.bind(null, 'name')}
          readOnly={!!data.is_archived}
          required={true}
          value={String(data.name || '')}
        />
      </Field>
      {/* Only show Theme Color if not in controlled mode */}
      {!controlledMode && (
        <Field label='Theme Color' style={{}}>
          <ColorPicker
            color={{ hex: String(data.theme_color || Colors.PRIMARY.hex) }}
            label='Theme Color'
            onChange={handleColorChange.bind(null, 'theme_color')}
          />
        </Field>
      )}
      <Field label='Member' style={{}}>
        <Select
          onChange={handleChange.bind(null, 'member_id')}
          options={member_names}
          placeholder='-- Select Member --'
          readOnly={!!data.is_archived}
          style={{}}
          value={String(data.member_id || '')}
        />
      </Field>
      <Field style={{}}>
        <Checkbox
          checked={!!data.hide_intro}
          disabled={!!data.is_archived}
          label='Hide the intro screen and display the case design screen by default'
          onChange={handleCheck.bind(null, 'hide_intro')}
        />
      </Field>
      <Field label='Required Fields' style={{}}>
        <Checklist
          disabled={!!data.is_archived}
          onChange={handleChecklistChange.bind(null, 'required_fields')}
          options={
            data &&
            data.required_fields_options.map(field => ({
              value: field,
              label: field.replaceAll('_', ' ')
            }))
          }
          selected={data.required_fields}
          style={{}}
        />
      </Field>
      <Field label='Token' style={{}}>
        <Input readOnly={true} value={String(token || '')} />
      </Field>
      <div style={{ marginBottom: 20 }}>
        <div style={sectionHeaderStyle}>Rate Limits</div>
        <Field label=''>
          <Checkbox
            checked={!!data.rate_limit_enabled}
            disabled={!!data.is_archived}
            label='Enabled'
            onChange={handleCheck.bind(null, 'rate_limit_enabled')}
          />
        </Field>

        <div style={{ display: 'flex', gap: 16 }}>
          <Field label='Token Limit' style={{ flex: 1 }}>
            <Input
              onChange={handleChange.bind(null, 'rate_limit_token_limit')}
              readOnly={rateLimitsReadOnly}
              type='number'
              value={String(data.rate_limit_token_limit || '')}
            />
          </Field>
          <Field label='Tokens Per Period' style={{ flex: 1 }}>
            <Input
              onChange={handleChange.bind(null, 'rate_limit_tokens_per_period')}
              readOnly={rateLimitsReadOnly}
              type='number'
              value={String(data.rate_limit_tokens_per_period || '')}
            />
          </Field>
        </div>

        <Field label='Replenishment Period Seconds' style={{ width: '49%' }}>
          <Input
            onChange={handleChange.bind(null, 'rate_limit_replenishment_period_seconds')}
            readOnly={rateLimitsReadOnly}
            type='number'
            value={String(data.rate_limit_replenishment_period_seconds || '')}
          />
        </Field>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={sectionHeaderStyle}>Monthly Usage</div>
        <Field label=''>
          <Checkbox
            checked={!!data.monthly_limit_enabled}
            disabled={!!data.is_archived}
            label='Enabled'
            onChange={handleCheck.bind(null, 'monthly_limit_enabled')}
          />
        </Field>

        <Field label='Monthly Request Limit' style={{ width: '49%' }}>
          <Input
            onChange={handleChange.bind(null, 'monthly_request_limit')}
            readOnly={monthlyUsageReadOnly}
            type='number'
            value={String(data.monthly_request_limit || '')}
          />
        </Field>
      </div>
      {/* Extra fields for Universal Quoter LTC Product Defaults */}
      {controlledMode && (
        <>
          <div style={{ display: 'flex', gap: 16 }}>
            <Field label='Date of Birth' style={{ flex: 1 }}>
              <DatePicker
                onChange={date => handleDateChange('dob', date)}
                readOnly={!!data.is_archived}
                value={String(data.dob || '')}
              />
            </Field>
            <Field label='Gender' style={{ flex: 1 }}>
              <Select
                onChange={handleChange.bind(null, 'gender')}
                options={GENDER_OPTIONS}
                readOnly={!!data.is_archived}
                style={{}}
                value={String(data.gender || GENDER_OPTIONS[0].value)}
              />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Field label='Age' style={{ flex: 1 }}>
              <Input
                onChange={handleChange.bind(null, 'age')}
                readOnly={!!data.is_archived}
                type='number'
                value={String(data.age || '')}
              />
            </Field>
            <Field label='Married' style={{ flex: 1 }}>
              <Select
                onChange={handleChange.bind(null, 'married')}
                options={YES_NO_OPTIONS}
                readOnly={!!data.is_archived}
                style={{}}
                value={String(data.married || YES_NO_OPTIONS[0].value)}
              />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Field label='State' style={{ flex: 1 }}>
              <Select
                onChange={handleChange.bind(null, 'state')}
                options={Constants.STATES}
                readOnly={!!data.is_archived}
                style={{}}
                value={String(data.state || Constants.STATES[0]?.value || '')}
              />
            </Field>
            <Field label='Tobacco' style={{ flex: 1 }}>
              <Select
                onChange={handleChange.bind(null, 'tobacco')}
                options={YES_NO_OPTIONS}
                readOnly={!!data.is_archived}
                style={{}}
                value={String(data.tobacco || YES_NO_OPTIONS[0].value)}
              />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Field label='Calculation Type' style={{ flex: 1 }}>
              <Select
                onChange={handleChange.bind(null, 'calculation_type')}
                options={CALC_TYPE_OPTIONS}
                readOnly={!!data.is_archived}
                style={{}}
                value={String(data.calculation_type || CALC_TYPE_OPTIONS[0].value)}
              />
            </Field>
            <Field label='Amount' style={{ flex: 1 }}>
              <Input
                onChange={handleChange.bind(null, 'amount')}
                readOnly={!!data.is_archived}
                type='number'
                value={String(data.amount || '')}
              />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Field label='Payment Schedule' style={{ flex: 1 }}>
              <Select
                onChange={handleChange.bind(null, 'payment_schedule')}
                options={PAYMENT_SCHEDULE_OPTIONS}
                readOnly={!!data.is_archived}
                style={{}}
                value={String(data.payment_schedule || PAYMENT_SCHEDULE_OPTIONS[0].value)}
              />
            </Field>
            <Field label='Payment Frequency' style={{ flex: 1 }}>
              <Select
                onChange={handleChange.bind(null, 'payment_frequency')}
                options={PAYMENT_SCHEDULE_OPTIONS}
                readOnly={!!data.is_archived}
                style={{}}
                value={String(data.payment_frequency || PAYMENT_SCHEDULE_OPTIONS[0].value)}
              />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Field label='Benefit Duration' style={{ flex: 1 }}>
              <Select
                onChange={handleChange.bind(null, 'benefit_duration')}
                options={BENEFIT_DURATION_OPTIONS}
                readOnly={!!data.is_archived}
                style={{}}
                value={String(data.benefit_duration || BENEFIT_DURATION_OPTIONS[0].value)}
              />
            </Field>
            <Field label='Lump Sum Premium' style={{ flex: 1 }}>
              <Input
                onChange={handleChange.bind(null, 'lump_sum_premium')}
                readOnly={!!data.is_archived}
                type='number'
                value={String(data.lump_sum_premium || '')}
              />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Field label='Refund of Premium' style={{ flex: 1 }}>
              <Select
                onChange={handleChange.bind(null, 'refund_of_premium')}
                options={REFUND_OPTIONS}
                readOnly={!!data.is_archived}
                style={{}}
                value={String(data.refund_of_premium || REFUND_OPTIONS[0].value)}
              />
            </Field>
            <Field label='Inflation Protection' style={{ flex: 1 }}>
              <Select
                onChange={handleChange.bind(null, 'inflation_protection')}
                options={INFLATION_OPTIONS}
                readOnly={!!data.is_archived}
                style={{}}
                value={String(data.inflation_protection || INFLATION_OPTIONS[0].value)}
              />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Field label='1035 Exchange' style={{ flex: 1 }}>
              <Select
                onChange={handleChange.bind(null, 'exchange_1035')}
                options={YES_NO_OPTIONS}
                readOnly={!!data.is_archived}
                style={{}}
                value={String(data.exchange_1035 || YES_NO_OPTIONS[1].value)}
              />
            </Field>
            <Field label='1035 Exchange Amount' style={{ flex: 1 }}>
              <Input
                onChange={handleChange.bind(null, 'exchange_1035_amount')}
                readOnly={!!data.is_archived}
                type='number'
                value={String(data.exchange_1035_amount || '')}
              />
            </Field>
          </div>
        </>
      )}
      <div style={{ display: 'flex' }}>
        <Button color='RED' onClick={handleArchiveToggle} style={{ marginRight: '5px' }}>
          {data.is_archived ? 'Unarchive' : 'Archive'}
        </Button>
        {data.is_archived ? null : (
          <Button onClick={handleSaveButton.bind(null, 'ltc_quoter')} style={{}}>
            Save Changes
          </Button>
        )}
      </div>
    </>
  );
};

General.propTypes = {
  controlledMode: PropTypes.bool,
  handleSave: PropTypes.func,
  ltc_quoter: PropTypes.object,
  token: PropTypes.string
};

export default General;
